import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { Download, Info, Table2 } from 'lucide-react';
import { toast } from 'sonner';
import { Page, PageBody, PageHeader, Panel, Stat, StatRow, Toolbar } from '@/ui/Panel';
import { DataTable, type Column } from '@/ui/DataTable';
import { ChipGroup, Pagination, SubNav, SubNavItem } from '@/ui/Filters';
import { Button, ButtonGroup } from '@/ui/Button';
import { Badge, Code } from '@/ui/Badge';
import { Input } from '@/ui/Field';
import { Callout } from '@/ui/Callout';
import { BarRank, Sparkline, TimeSeries, type BarRankRow } from '@/ui/Chart';
import { MultiSelect, parseIdCsv, toIdCsv } from '@/ui/MultiSelect';
import { api, errorMessage, saveBlob, type QueryParams } from '@/lib/api/client';
import type {
  BrandSummary,
  DepartmentListResponse,
  PaginatedResponse,
  VendorListResponse,
} from '@/lib/api/types';
import { humanize, isoDate, money, moneyCompact, num, percent, qty } from '@/lib/format';
import { useShortcutHint } from '@/app/StatusLine';
import { asEnum, asText } from '@/lib/searchParams';
import {
  GRANULARITIES,
  HOURLY_MAX_DAYS,
  effectiveGranularity,
  fillBuckets,
  formatBucketAxis,
  formatBucketFull,
  rangeDays,
  type Granularity,
  type SalesBucket,
} from '@/features/reports/buckets';
import { grossMargin as marginOnSales, salesLabel } from '@/features/reports/sales-label';
import {
  FREIGHT_GROUPS,
  FreightReportView,
  MarginReportView,
} from '@/features/reports/freight-reports';
import type { FreightGroupBy } from '@/features/buying/freight/types';
import {
  FRESH_GROUPS,
  FRESH_IN_OUT_SUBTITLE,
  FreshInOutView,
  type FreshGroupBy,
} from '@/features/reports/fresh-in-out';

/**
 * Reports — one screen, ten reports (Freight and Margin with freight: TASK-0258; Fresh in / out:
 * TASK-0259).
 *
 * The old admin shipped these as disconnected pages, so answering "why is that
 * department's margin down?" meant re-entering the same date range four times.
 * Here the report is a tab in the URL and the date range and the department /
 * brand / vendor filters are *shared state*: switch from Sales to Financial to
 * Products and you are still looking at the same slice of the business.
 *
 * Every endpoint accepts `format=csv`, so every tab exports — not just
 * Products, which was the only one the old admin wired up.
 *
 * Two tabs do not answer the same question as the other five:
 *   · **Tenders** splits the same completed sales by how they were paid — the
 *     "how much of today is cash" question that drives the deposit run.
 *   · **Voids** is the deliberate inverse: the voided and refunded tickets that
 *     `REPORT_STATUSES` excludes from every other tab on this screen.
 * Both are header-scoped on the server (no `transaction_items` join), so the
 * catalog filters do not reach them and the filter bar drops those controls
 * rather than showing three selects that quietly do nothing.
 */

/* ── Contract notes (verified against server/api/src/modules/reports/routes.ts)
   `types.ts#ReportRow` is the lowest common denominator and is missing `id`,
   which products / inventory / cashiers all return. The row types below are the
   real per-endpoint shapes.                                                  */

/** The sales row is a bucket: `label` is a day, or an hour under
 *  `granularity=hour`. See `@/features/reports/buckets`. */
type SalesRow = SalesBucket;
type FinancialRow = { label: string; revenue: number; cogs: number; tax: number };
/** products + inventory: `count` is a Postgres numeric, so it may be fractional. */
type RankedRow = { id: number; label: string; count: number; revenue: number };
/** `id` is null for the synthetic unassigned bucket. `is_unassigned` is the one
 *  snake_case field in an otherwise camelCase API. */
type CashierRow = {
  id: number | null;
  label: string;
  revenue: number;
  count: number;
  is_unassigned: boolean;
};
/** `count` is tender *rows*; `transactionCount` is distinct receipts. They
 *  differ when one receipt took two tenders of the same type. `share` is a
 *  0..1 fraction of tendered value, rounded server-side to 4dp. */
type TenderRow = {
  label: string;
  count: number;
  transactionCount: number;
  revenue: number;
  share: number;
};
/** Voided totals stay positive; refund totals are negative, and the server
 *  keeps them in separate columns precisely so they never cancel out. */
type VoidRow = {
  id: number | null;
  label: string;
  voidCount: number;
  voidTotal: number;
  refundCount: number;
  refundTotal: number;
  count: number;
  is_unassigned: boolean;
};

const REPORTS = [
  'sales',
  'products',
  'inventory',
  'financial',
  'cashiers',
  'tenders',
  'voids',
  'freight',
  'freight-margin',
  'fresh-in-out',
] as const;
type ReportTab = (typeof REPORTS)[number];

const TAB_LABEL: Record<ReportTab, string> = {
  sales: 'Sales',
  products: 'Products',
  inventory: 'Inventory',
  financial: 'Financial',
  cashiers: 'Cashiers',
  tenders: 'Tenders',
  voids: 'Voids',
  freight: 'Freight',
  'freight-margin': 'Margin with freight',
  'fresh-in-out': 'Fresh in / out',
};

/**
 * Which shared filters each tab actually honours.
 *
 * Inventory is a point-in-time snapshot: the endpoint reads current on-hand and
 * ignores every filter except `format`. Tenders and Voids are header-scoped —
 * `/reports/tenders` aggregates `transaction_tenders` and `/reports/voids`
 * aggregates transaction headers, neither joins `transaction_items`, so a
 * department / brand / vendor filter changes nothing at all (verified against
 * the live API: identical rows with and without `department_id`).
 *
 * Showing filter controls that silently do nothing is exactly the lie this
 * console exists to stop telling, so the bar drops the ones that don't apply.
 */
const TAB_SCOPE: Record<ReportTab, { dates: boolean; catalog: boolean }> = {
  sales: { dates: true, catalog: true },
  products: { dates: true, catalog: true },
  inventory: { dates: false, catalog: false },
  financial: { dates: true, catalog: true },
  cashiers: { dates: true, catalog: true },
  tenders: { dates: true, catalog: false },
  voids: { dates: true, catalog: false },
  // TASK-0258 (DROP-2b): their own grouping / department pick in the report body.
  freight: { dates: true, catalog: false },
  'freight-margin': { dates: true, catalog: false },
  // TASK-0259 (FRESH-1): its own family / department grouping and family filter in the report body.
  'fresh-in-out': { dates: true, catalog: false },
};

/** The per-tab promise in the page header. Voids inverts the usual one. */
const TAB_SUBTITLE: Record<ReportTab, string> = {
  sales:
    'Completed sales and refunds only — voided, suspended and training tickets are excluded.',
  products:
    'Completed sales and refunds only — voided, suspended and training tickets are excluded.',
  inventory: 'A live snapshot of on-hand stock, valued at each product’s current cost.',
  financial:
    'Completed sales and refunds only — voided, suspended and training tickets are excluded.',
  cashiers:
    'Completed sales and refunds only — voided, suspended and training tickets are excluded.',
  tenders:
    'How completed sales were paid. A receipt split across two tenders is counted in both rows.',
  voids:
    'Voided and refunded tickets only — the activity every other tab on this screen leaves out.',
  freight:
    'What it cost to get goods onto our shelf. Margins elsewhere use cost; this report adds freight.',
  'freight-margin':
    'Completed sales and refunds, at cost and with freight. Margins elsewhere use cost; this report adds freight.',
  'fresh-in-out': FRESH_IN_OUT_SUBTITLE,
};

type SortKey = 'revenue' | 'units' | 'name';

interface ReportsSearch {
  report: ReportTab;
  from: string;
  to: string;
  dept: string;
  brand: string;
  vendor: string;
  page: number;
  sort: SortKey;
  order: 'asc' | 'desc';
  /** Sales only. Kept in the URL even when the range is too wide to honour it,
   *  so narrowing the range brings the operator's choice back by itself. */
  granularity: Granularity;
  /** Freight only: how its rows are grouped. */
  group: FreightGroupBy;
  /** Margin with freight only: one department, or every one (''). */
  mdept: string;
  /** Fresh in / out only (TASK-0259): rows by family or by department, and a CSV of family codes. */
  fgroup: FreshGroupBy;
  ffam: string;
}

// Evaluated once per session so the default range is stable across renders and
// two components never disagree about what "today" is mid-render.
const TODAY = isoDate(new Date());
const THIRTY_DAYS_AGO = isoDate(new Date(Date.now() - 29 * 86_400_000));

export const Route = createFileRoute('/_app/reports')({
  staticData: { crumb: 'Reports' },
  validateSearch: (s: Record<string, unknown>): ReportsSearch => {
    const report = REPORTS.includes(s.report as ReportTab)
      ? (s.report as ReportTab)
      : 'sales';
    const sort = ['revenue', 'units', 'name'].includes(s.sort as string)
      ? (s.sort as SortKey)
      : 'revenue';
    return {
      report,
      from: asText(s.from, THIRTY_DAYS_AGO),
      to: asText(s.to, TODAY),
      dept: asText(s.dept, ''),
      brand: asText(s.brand, ''),
      vendor: asText(s.vendor, ''),
      page: Number(s.page) > 0 ? Number(s.page) : 1,
      sort,
      order: s.order === 'asc' ? 'asc' : 'desc',
      granularity: asEnum(s.granularity, GRANULARITIES, 'day'),
      group: asEnum(s.group, FREIGHT_GROUPS, 'location'),
      mdept: /^\d+$/.test(String(s.mdept ?? '')) ? String(s.mdept) : '',
      fgroup: asEnum(s.fgroup, FRESH_GROUPS, 'family'),
      ffam: /^[a-z_,]*$/.test(String(s.ffam ?? '')) ? String(s.ffam ?? '') : '',
    };
  },
  component: ReportsScreen,
});

/* ── Query construction ───────────────────────────────────────────────────── */

function filterParams(s: ReportsSearch, tab: ReportTab): QueryParams {
  const scope = TAB_SCOPE[tab];
  const catalog = scope.dates && scope.catalog;
  return {
    date_from: scope.dates ? s.from : undefined,
    date_to: scope.dates ? s.to : undefined,
    department_id: catalog && s.dept ? s.dept : undefined,
    brand_id: catalog && s.brand ? s.brand : undefined,
    vendor_id: catalog && s.vendor ? s.vendor : undefined,
  };
}

const PAGE_SIZE = 50;

/* ── Screen ───────────────────────────────────────────────────────────────── */

function ReportsScreen() {
  const search = Route.useSearch();
  const navigate = Route.useNavigate();

  const setSearch = useCallback(
    (patch: Partial<ReportsSearch>) => {
      void navigate({
        search: (prev: ReportsSearch) => ({ ...prev, ...patch }),
        replace: true,
      });
    },
    [navigate]
  );

  // 1–7 jump between reports. The status line advertises it.
  useShortcutHint([
    { keys: '1–9', label: 'Report' },
    { keys: 'E', label: 'Export CSV' },
  ]);

  const [exporting, setExporting] = useState(false);
  const scope = TAB_SCOPE[search.report];
  // What the Sales tab will really draw — an hourly request over a wide range
  // collapses to daily, and the export must match what is on screen.
  const granularity = effectiveGranularity(search.granularity, search.from, search.to);

  const exportCsv = useCallback(async () => {
    setExporting(true);
    try {
      const hourly = search.report === 'sales' && granularity === 'hour';
      // TASK-0258: the freight reports take their own day and grouping parameters.
      const { blob } =
        search.report === 'freight'
          ? await api.download('/reports/freight', {
              params: {
                from: search.from,
                to: search.to,
                groupBy: search.group,
                format: 'csv',
              },
            })
          : search.report === 'freight-margin'
            ? await api.download('/reports/landed-margin', {
                params: {
                  from: search.from,
                  to: search.to,
                  departmentId: search.mdept || undefined,
                  format: 'csv',
                },
              })
            : search.report === 'fresh-in-out'
              ? await api.download('/reports/fresh-in-out', {
                  params: {
                    from: search.from,
                    to: search.to,
                    groupBy: search.fgroup,
                    family: search.ffam || undefined,
                    format: 'csv',
                  },
                })
              : await api.download(`/reports/${search.report}`, {
                  params: {
                    ...filterParams(search, search.report),
                    granularity: hourly ? 'hour' : undefined,
                    format: 'csv',
                  },
                });
      // The endpoint sets content-type but no content-disposition, so the
      // filename has to come from here or every download lands as "export.csv".
      const stamp = scope.dates ? `${search.from}_${search.to}` : TODAY;
      saveBlob(blob, `kk-${search.report}${hourly ? '-hourly' : ''}-${stamp}.csv`);
      toast.success(`${TAB_LABEL[search.report]} report exported`);
    } catch (err) {
      toast.error('Export failed', { description: errorMessage(err) });
    } finally {
      setExporting(false);
    }
  }, [search, scope.dates, granularity]);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement | null;
      if (
        e.metaKey ||
        e.ctrlKey ||
        e.altKey ||
        (t &&
          (t.tagName === 'INPUT' ||
            t.tagName === 'TEXTAREA' ||
            t.tagName === 'SELECT' ||
            t.isContentEditable))
      ) {
        return;
      }
      const idx = Number(e.key);
      if (idx >= 1 && idx <= REPORTS.length) {
        e.preventDefault();
        setSearch({ report: REPORTS[idx - 1]!, page: 1 });
      } else if (e.key.toLowerCase() === 'e') {
        e.preventDefault();
        void exportCsv();
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [setSearch, exportCsv]);

  return (
    <Page>
      <PageHeader
        eyebrow="Insight"
        title="Reports"
        subtitle={TAB_SUBTITLE[search.report]}
        actions={
          <Button
            size="sm"
            icon={<Download className="size-3.5" />}
            loading={exporting}
            onClick={() => void exportCsv()}
            kbd="E"
          >
            Export CSV
          </Button>
        }
        tabs={
          <SubNav>
            {REPORTS.map((r) => (
              <SubNavItem
                key={r}
                active={search.report === r}
                onClick={() => setSearch({ report: r, page: 1 })}
              >
                {TAB_LABEL[r]}
              </SubNavItem>
            ))}
          </SubNav>
        }
      />

      <FilterBar search={search} setSearch={setSearch} />

      <PageBody className="flex flex-col gap-3">
        {search.report === 'sales' ? (
          <SalesReport search={search} setSearch={setSearch} granularity={granularity} />
        ) : null}
        {search.report === 'products' ? (
          <ProductsReport search={search} setSearch={setSearch} />
        ) : null}
        {search.report === 'inventory' ? <InventoryReport /> : null}
        {search.report === 'financial' ? <FinancialReport search={search} /> : null}
        {search.report === 'cashiers' ? <CashiersReport search={search} /> : null}
        {search.report === 'tenders' ? <TendersReport search={search} /> : null}
        {search.report === 'voids' ? <VoidsReport search={search} /> : null}
        {search.report === 'freight' ? (
          <FreightReportView
            from={search.from}
            to={search.to}
            group={search.group}
            onGroup={(group) => setSearch({ group })}
          />
        ) : null}
        {search.report === 'freight-margin' ? (
          <MarginReportView
            from={search.from}
            to={search.to}
            departmentId={search.mdept ? Number(search.mdept) : null}
            onDepartment={(id) => setSearch({ mdept: id === null ? '' : String(id) })}
          />
        ) : null}
        {search.report === 'fresh-in-out' ? (
          <FreshInOutView
            from={search.from}
            to={search.to}
            groupBy={search.fgroup}
            family={search.ffam}
            onGroupBy={(fgroup) => setSearch({ fgroup })}
            onFamily={(ffam) => setSearch({ ffam })}
          />
        ) : null}
      </PageBody>
    </Page>
  );
}

/* ── Shared filter bar ────────────────────────────────────────────────────── */

const RANGES: Array<{ label: string; days?: number; kind?: 'mtd' | 'ytd' }> = [
  { label: 'Today', days: 0 },
  { label: '7d', days: 6 },
  { label: '30d', days: 29 },
  { label: 'MTD', kind: 'mtd' },
  { label: 'YTD', kind: 'ytd' },
];

function FilterBar({
  search,
  setSearch,
}: {
  search: ReportsSearch;
  setSearch: (patch: Partial<ReportsSearch>) => void;
}) {
  const scope = TAB_SCOPE[search.report];
  const departments = useQuery({
    queryKey: ['departments', 'options'],
    queryFn: () =>
      api.get<DepartmentListResponse>('/departments', { params: { pageSize: 500 } }),
    staleTime: 10 * 60_000,
    retry: false,
  });
  const brands = useQuery({
    queryKey: ['brands', 'options'],
    queryFn: () => api.get<BrandSummary[]>('/brands'),
    staleTime: 10 * 60_000,
    retry: false,
  });
  const vendors = useQuery({
    queryKey: ['vendors', 'options'],
    queryFn: () => api.get<VendorListResponse>('/vendors', { params: { pageSize: 500 } }),
    staleTime: 10 * 60_000,
    retry: false,
  });

  function applyRange(r: (typeof RANGES)[number]) {
    const now = new Date();
    if (r.kind === 'mtd') {
      setSearch({
        from: isoDate(new Date(now.getFullYear(), now.getMonth(), 1)),
        to: TODAY,
        page: 1,
      });
      return;
    }
    if (r.kind === 'ytd') {
      setSearch({
        from: isoDate(new Date(now.getFullYear(), 0, 1)),
        to: TODAY,
        page: 1,
      });
      return;
    }
    setSearch({
      from: isoDate(new Date(Date.now() - (r.days ?? 0) * 86_400_000)),
      to: TODAY,
      page: 1,
    });
  }

  if (!scope.dates) {
    return (
      <Toolbar>
        <span className="text-ink-muted text-xs">
          Inventory is a live snapshot of tracked products — no date range applies.
        </span>
      </Toolbar>
    );
  }

  const catalogSet = Boolean(search.dept || search.brand || search.vendor);

  return (
    <Toolbar>
      <div className="flex items-center gap-1.5">
        <Input
          type="date"
          size="sm"
          mono
          value={search.from}
          max={search.to}
          onChange={(e) => setSearch({ from: e.currentTarget.value, page: 1 })}
          className="w-36"
          aria-label="From date"
        />
        <span className="text-2xs text-ink-faint">to</span>
        <Input
          type="date"
          size="sm"
          mono
          value={search.to}
          min={search.from}
          onChange={(e) => setSearch({ to: e.currentTarget.value, page: 1 })}
          className="w-36"
          aria-label="To date"
        />
      </div>

      <ButtonGroup>
        {RANGES.map((r) => (
          <Button key={r.label} size="sm" onClick={() => applyRange(r)}>
            {r.label}
          </Button>
        ))}
      </ButtonGroup>

      {search.report === 'sales' ? (
        <ChipGroup<Granularity>
          value={search.granularity}
          onChange={(granularity) => setSearch({ granularity })}
          options={[
            { value: 'day', label: 'Day' },
            { value: 'hour', label: 'Hour' },
          ]}
        />
      ) : null}

      {scope.catalog ? (
        <div className="ml-auto flex flex-wrap items-center gap-1.5">
          <MultiSelect
            label="Dept"
            loading={departments.isLoading}
            options={(departments.data?.data ?? []).map((d) => ({
              value: d.id,
              label: d.name,
              hint: d.code,
            }))}
            selected={parseIdCsv(search.dept)}
            onChange={(next) => setSearch({ dept: toIdCsv(next), page: 1 })}
          />
          <MultiSelect
            label="Brand"
            loading={brands.isLoading}
            options={(brands.data ?? []).map((b) => ({
              value: b.id,
              label: b.name,
              hint: b.productCount === undefined ? undefined : String(b.productCount),
            }))}
            selected={parseIdCsv(search.brand)}
            onChange={(next) => setSearch({ brand: toIdCsv(next), page: 1 })}
          />
          <MultiSelect
            label="Vendor"
            loading={vendors.isLoading}
            options={(vendors.data?.data ?? []).map((v) => ({
              value: v.id,
              label: v.name,
            }))}
            selected={parseIdCsv(search.vendor)}
            onChange={(next) => setSearch({ vendor: toIdCsv(next), page: 1 })}
          />
          {catalogSet ? (
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setSearch({ dept: '', brand: '', vendor: '', page: 1 })}
            >
              Clear
            </Button>
          ) : null}
        </div>
      ) : (
        /* The tab keeps the operator's catalog filters in the URL — switching
           back to Products restores them — but must not imply they are in
           force here. */
        <span className="text-ink-faint ml-auto text-2xs">
          {search.report === 'freight' || search.report === 'freight-margin'
            ? // TASK-0258: the freight reports group (and pick a department) in the report itself.
              `${TAB_LABEL[search.report]} uses the dates here; its own grouping is below${catalogSet ? ', and the department, brand and vendor filters are held for the other tabs' : ''}.`
            : catalogSet
              ? `${TAB_LABEL[search.report]} counts whole tickets, so the department, brand and vendor filters do not apply and are held for the other tabs.`
              : `${TAB_LABEL[search.report]} counts whole tickets — dates only.`}
        </span>
      )}
    </Toolbar>
  );
}

/** Any of the three catalog filters pulls the API into item-level aggregation,
 *  which changes what "sales" means. Say so rather than let it surprise. */
function ScopeNote({ search }: { search: ReportsSearch }) {
  if (!search.dept && !search.brand && !search.vendor) return null;
  return (
    <Callout tone="info" icon={<Info />} title="Filtered to matching line items">
      With a department, brand or vendor filter applied the API aggregates from
      transaction lines rather than ticket headers: sales (before tax) and tax cover only
      the matching lines, and transaction counts are distinct tickets that contained at
      least one. These figures will not add up to the unfiltered store totals.
    </Callout>
  );
}

/**
 * What the API's `revenue` figure is (TASK-0226). Without a catalog filter the
 * sales, financial and cashier reports sum ticket totals, which include sales
 * tax. With a department, brand or vendor filter — and always on Products — they
 * sum line prices, which do not. Say which, instead of calling both "Revenue".
 */
function isCatalogScoped(search: ReportsSearch): boolean {
  return Boolean(search.dept || search.brand || search.vendor);
}

/* ── Sales ────────────────────────────────────────────────────────────────── */

function SalesReport({
  search,
  setSearch,
  granularity,
}: {
  search: ReportsSearch;
  setSearch: (patch: Partial<ReportsSearch>) => void;
  granularity: Granularity;
}) {
  const hourly = granularity === 'hour';
  const sales = salesLabel(!isCatalogScoped(search));
  const params: QueryParams = {
    ...filterParams(search, 'sales'),
    granularity: hourly ? 'hour' : undefined,
  };
  const q = useQuery({
    queryKey: ['reports', 'sales', params],
    queryFn: () => api.get<SalesRow[]>('/reports/sales', { params }),
  });

  const rows = useMemo(() => q.data ?? [], [q.data]);
  const totals = useMemo(
    () =>
      rows.reduce(
        (acc, r) => ({
          revenue: acc.revenue + r.revenue,
          count: acc.count + r.count,
          tax: acc.tax + r.tax,
        }),
        { revenue: 0, count: 0, tax: 0 }
      ),
    [rows]
  );
  // Buckets the server actually returned — it emits none for a closed hour or a
  // day with no sales, so this is "buckets that traded", not elapsed time.
  const buckets = rows.length;
  const spanDays = rangeDays(search.from, search.to);
  const perBucket = hourly ? 'hour' : 'day';

  // The chart gets the empty buckets back so its x-axis stays linear in time;
  // the table and the CSV stay exactly what the server said.
  const plotted = useMemo(() => fillBuckets(rows, granularity), [rows, granularity]);
  const plotLabels = useMemo(() => plotted.map((r) => r.label), [plotted]);
  // The axis carries three labels, so they must read as clock times, not ISO.
  // The hover readout re-derives the full label from the same index.
  const axisLabels = useMemo(
    () => plotLabels.map((label) => formatBucketAxis(label, plotLabels)),
    [plotLabels]
  );

  const columns: Array<Column<SalesRow>> = [
    {
      id: 'label',
      header: hourly ? 'Hour' : 'Day',
      cell: (r) => formatBucketFull(r.label),
      mono: true,
      width: hourly ? '13rem' : '10rem',
    },
    {
      id: 'count',
      header: 'Tickets',
      cell: (r) => num(r.count),
      mono: true,
      align: 'right',
      width: '8rem',
    },
    {
      id: 'tax',
      header: 'Tax',
      cell: (r) => money(r.tax),
      mono: true,
      align: 'right',
      width: '9rem',
    },
    {
      id: 'avg',
      header: 'Avg ticket',
      cell: (r) => (r.count ? money(Math.round(r.revenue / r.count)) : '—'),
      mono: true,
      align: 'right',
      width: '9rem',
      hideBelow: 'lg',
    },
    {
      id: 'revenue',
      header: sales,
      cell: (r) => (
        <span className={r.revenue < 0 ? 'text-negative' : undefined}>
          {money(r.revenue)}
        </span>
      ),
      mono: true,
      align: 'right',
      width: '10rem',
    },
  ];

  return (
    <>
      <ScopeNote search={search} />
      <GranularityNote search={search} setSearch={setSearch} granularity={granularity} />

      <StatRow>
        <Stat
          label={sales}
          value={money(totals.revenue)}
          sub={`${num(buckets)} ${perBucket}${buckets === 1 ? '' : 's'} with sales`}
        />
        <Stat label="Transactions" value={num(totals.count)} />
        <Stat label="Tax collected" value={money(totals.tax)} />
        <Stat
          label={hourly ? 'Average per hour' : 'Average per day'}
          value={buckets ? money(Math.round(totals.revenue / buckets)) : '—'}
          sub={`over the ${num(buckets)} that traded`}
        />
      </StatRow>

      <Panel
        title={hourly ? `${sales} per hour` : `${sales} per day`}
        description={
          hourly
            ? `${search.from} → ${search.to} · hours on the store's clock, wherever you are reading this`
            : `${search.from} → ${search.to}`
        }
      >
        <TimeSeries
          labels={axisLabels}
          series={[
            {
              id: 'revenue',
              label: sales,
              tone: 'signal',
              values: plotted.map((r) => r.revenue),
            },
          ]}
          format={moneyCompact}
          formatLabel={(_, i) => formatBucketFull(plotLabels[i] ?? '')}
        />
      </Panel>

      <Panel
        title={hourly ? 'Hourly detail' : 'Daily detail'}
        description={
          hourly && plotted.length > rows.length
            ? `${num(plotted.length - rows.length)} hours in this window took no sales; they are drawn as zero above and left out below, so the table matches the export.`
            : undefined
        }
        bleed
        actions={
          rows.length > 1 ? (
            <span className="text-2xs text-ink-faint flex items-center gap-2">
              Tickets
              <Sparkline
                values={plotted.map((r) => r.count)}
                tone="info"
                title={`Transactions per ${perBucket}`}
              />
            </span>
          ) : null
        }
      >
        <DataTable
          rows={q.data}
          columns={columns}
          rowKey={(r) => r.label}
          loading={q.isLoading}
          error={q.error}
          onRetry={() => void q.refetch()}
          keyboard={false}
          empty={{
            icon: <Table2 />,
            title: 'No completed sales in this range',
            description: 'Widen the date range or clear the catalog filters.',
          }}
          footer={
            rows.length ? (
              <span className="font-mono tabular-nums">
                {num(buckets)} {perBucket}
                {buckets === 1 ? '' : 's'} of {num(spanDays)} day
                {spanDays === 1 ? '' : 's'} · {num(totals.count)} tickets ·{' '}
                {money(totals.revenue)}
              </span>
            ) : null
          }
        />
      </Panel>
    </>
  );
}

/**
 * Why the Day / Hour choice did or did not take effect.
 *
 * The rule: hourly is offered for any range, but only *drawn* for
 * HOURLY_MAX_DAYS or fewer. Past that the screen collapses to daily and says
 * so here — the choice stays in the URL, so narrowing the range restores it
 * without the operator having to ask for hourly twice. Silently ignoring the
 * toggle, or drawing four hundred buckets on an axis that carries three
 * labels, are the two failures this note exists to avoid.
 */
function GranularityNote({
  search,
  setSearch,
  granularity,
}: {
  search: ReportsSearch;
  setSearch: (patch: Partial<ReportsSearch>) => void;
  granularity: Granularity;
}) {
  if (search.granularity !== 'hour' || granularity === 'hour') return null;
  const days = rangeDays(search.from, search.to);
  const lastWeek = isoDate(new Date(Date.now() - (HOURLY_MAX_DAYS - 1) * 86_400_000));

  return (
    <Callout
      tone="warn"
      icon={<Info />}
      title={`Showing days — hourly needs ${HOURLY_MAX_DAYS} days or fewer`}
      actions={
        <Button
          size="xs"
          onClick={() => setSearch({ from: lastWeek, to: TODAY, page: 1 })}
        >
          Last {HOURLY_MAX_DAYS} days
        </Button>
      }
    >
      You asked for hourly buckets across {days > 0 ? `${num(days)} days` : 'this range'}.
      Hourly answers an intraday question — when the rush lands, when the drawer can be
      pulled — and that shape disappears once the chart is carrying weeks of overnight
      troughs. Hour is still selected: narrow the range and it takes effect.
    </Callout>
  );
}

/* ── Financial ────────────────────────────────────────────────────────────── */

function FinancialReport({ search }: { search: ReportsSearch }) {
  const params = filterParams(search, 'financial');
  const q = useQuery({
    queryKey: ['reports', 'financial', params],
    queryFn: () => api.get<FinancialRow[]>('/reports/financial', { params }),
  });

  const rows = useMemo(() => q.data ?? [], [q.data]);
  const totals = useMemo(
    () =>
      rows.reduce(
        (acc, r) => ({
          revenue: acc.revenue + r.revenue,
          cogs: acc.cogs + r.cogs,
          tax: acc.tax + r.tax,
        }),
        { revenue: 0, cogs: 0, tax: 0 }
      ),
    [rows]
  );
  // Margin is taken on sales before tax. Ticket totals (no catalog filter)
  // include the sales tax collected, which is the state's money, not margin.
  const includesTax = !isCatalogScoped(search);
  const sales = salesLabel(includesTax);
  type MoneyRow = Pick<FinancialRow, 'revenue' | 'tax' | 'cogs'>;
  const marginOf = (r: MoneyRow) => marginOnSales(r, includesTax).amount;
  const marginPctOf = (r: MoneyRow) => marginOnSales(r, includesTax).pct;
  const grossMargin = marginOf(totals);
  const marginPct = marginPctOf(totals);

  const columns: Array<Column<FinancialRow>> = [
    { id: 'label', header: 'Day', cell: (r) => r.label, mono: true, width: '10rem' },
    {
      id: 'revenue',
      header: sales,
      cell: (r) => money(r.revenue),
      mono: true,
      align: 'right',
      width: '10rem',
    },
    {
      id: 'cogs',
      header: 'COGS',
      cell: (r) => money(Math.round(r.cogs)),
      mono: true,
      align: 'right',
      width: '10rem',
    },
    {
      id: 'margin',
      header: 'Gross margin',
      cell: (r) => {
        const m = marginOf(r);
        return (
          <span className={m < 0 ? 'text-negative' : undefined}>
            {money(Math.round(m))}
          </span>
        );
      },
      mono: true,
      align: 'right',
      width: '10rem',
    },
    {
      id: 'marginPct',
      header: 'Margin %',
      cell: (r) => {
        const pct = marginPctOf(r);
        return pct === null ? '—' : percent(pct);
      },
      mono: true,
      align: 'right',
      width: '8rem',
      hideBelow: 'lg',
    },
    {
      id: 'tax',
      header: 'Tax',
      cell: (r) => money(r.tax),
      mono: true,
      align: 'right',
      width: '9rem',
    },
  ];

  return (
    <>
      <ScopeNote search={search} />
      <Callout tone="neutral" icon={<Info />} title="How COGS and margin are derived">
        COGS multiplies each sold line by the product's current cost, not the cost at the
        time of sale. A product repriced since the sale shifts its historic margin. Treat
        margin here as directional, not as a closed book.
        {includesTax
          ? ' Sales here are ticket totals, which include the sales tax collected; margin is worked out on sales minus that tax.'
          : ' With a catalog filter, sales are line prices before tax, and margin is worked out on them.'}
      </Callout>

      <StatRow>
        <Stat label={sales} value={money(totals.revenue)} />
        <Stat label="COGS" value={money(Math.round(totals.cogs))} />
        <Stat
          label="Gross margin"
          value={money(Math.round(grossMargin))}
          tone={grossMargin < 0 ? 'negative' : undefined}
          sub={marginPct === null ? undefined : percent(marginPct)}
        />
        <Stat label="Tax collected" value={money(totals.tax)} />
      </StatRow>

      <Panel title="Sales against cost" description={`${search.from} → ${search.to}`}>
        <TimeSeries
          labels={rows.map((r) => r.label)}
          series={[
            {
              id: 'revenue',
              label: sales,
              tone: 'signal',
              values: rows.map((r) => r.revenue),
            },
            {
              id: 'cogs',
              label: 'COGS',
              tone: 'info',
              values: rows.map((r) => Math.round(r.cogs)),
            },
          ]}
          format={moneyCompact}
        />
      </Panel>

      <Panel
        title="Daily detail"
        bleed
        actions={
          rows.length > 1 ? (
            <span className="text-2xs text-ink-faint flex items-center gap-2">
              Margin %
              <Sparkline
                values={rows.map((r) => marginPctOf(r) ?? 0)}
                tone="positive"
                title="Gross margin percentage per day"
              />
            </span>
          ) : null
        }
      >
        <DataTable
          rows={q.data}
          columns={columns}
          rowKey={(r) => r.label}
          loading={q.isLoading}
          error={q.error}
          onRetry={() => void q.refetch()}
          keyboard={false}
          empty={{
            icon: <Table2 />,
            title: 'No completed sales in this range',
            description: 'Widen the date range or clear the catalog filters.',
          }}
          footer={
            rows.length ? (
              <span className="font-mono tabular-nums">
                {money(totals.revenue)} {sales.toLowerCase()} ·{' '}
                {money(Math.round(totals.cogs))} cost · {money(Math.round(grossMargin))}{' '}
                margin
                {marginPct === null ? '' : ` (${percent(marginPct)})`}
              </span>
            ) : null
          }
        />
      </Panel>
    </>
  );
}

/* ── Products ─────────────────────────────────────────────────────────────── */

function ProductsReport({
  search,
  setSearch,
}: {
  search: ReportsSearch;
  setSearch: (patch: Partial<ReportsSearch>) => void;
}) {
  const params: QueryParams = {
    ...filterParams(search, 'products'),
    page: search.page,
    pageSize: PAGE_SIZE,
    sort: search.sort,
    order: search.order,
  };
  const q = useQuery({
    queryKey: ['reports', 'products', params],
    queryFn: () => api.get<PaginatedResponse<RankedRow>>('/reports/products', { params }),
  });

  const rows = q.data?.data ?? [];
  const total = q.data?.total ?? 0;
  const pageRevenue = rows.reduce((a, r) => a + r.revenue, 0);

  const rank: BarRankRow[] = rows.slice(0, 15).map((r) => ({
    id: r.id,
    label: r.label,
    value: r.revenue,
    hint: `${qty(r.count)} units sold, returns netted`,
  }));
  // Products always sums line prices, which exclude tax (TASK-0226).
  const sales = salesLabel(false);

  const columns: Array<Column<RankedRow>> = [
    { id: 'name', header: 'Product', cell: (r) => r.label, sortable: true },
    {
      id: 'units',
      header: 'Units',
      cell: (r) => qty(r.count),
      mono: true,
      align: 'right',
      width: '9rem',
      sortable: true,
    },
    {
      id: 'avg',
      header: 'Avg price',
      cell: (r) => (r.count ? money(Math.round(r.revenue / r.count)) : '—'),
      mono: true,
      align: 'right',
      width: '9rem',
      hideBelow: 'lg',
    },
    {
      id: 'revenue',
      header: sales,
      cell: (r) => money(r.revenue),
      mono: true,
      align: 'right',
      width: '10rem',
      sortable: true,
    },
  ];

  return (
    <>
      <ScopeNote search={search} />
      <StatRow>
        <Stat label="Products sold" value={num(total)} sub="distinct SKUs" />
        <Stat
          label={`${sales}, this page`}
          value={money(pageRevenue)}
          sub={`top ${rows.length}`}
        />
        <Stat
          label="Units this page"
          value={qty(rows.reduce((a, r) => a + r.count, 0))}
        />
      </StatRow>

      <Panel
        title={`Top ${rank.length} by ${search.sort === 'units' ? 'units' : 'sales'}`}
        description="Page order — change the sort or page to re-rank"
      >
        <BarRank
          rows={
            search.sort === 'units'
              ? rows.slice(0, 15).map((r) => ({
                  id: r.id,
                  label: r.label,
                  value: r.count,
                  hint: `${money(r.revenue)} ${sales.toLowerCase()}`,
                }))
              : rank
          }
          format={search.sort === 'units' ? qty : moneyCompact}
        />
      </Panel>

      <Panel title="All products" bleed className="min-h-72">
        <DataTable
          rows={q.data?.data}
          columns={columns}
          rowKey={(r) => r.id}
          loading={q.isLoading}
          error={q.error}
          onRetry={() => void q.refetch()}
          keyboard={false}
          sort={{
            by:
              search.sort === 'revenue'
                ? 'revenue'
                : search.sort === 'units'
                  ? 'units'
                  : 'name',
            dir: search.order,
            onChange: (by, dir) =>
              setSearch({
                sort: by === 'units' ? 'units' : by === 'name' ? 'name' : 'revenue',
                order: dir,
                page: 1,
              }),
          }}
          empty={{
            icon: <Table2 />,
            title: 'No products sold in this range',
            description: 'Widen the date range or clear the catalog filters.',
          }}
          footer={
            <Pagination
              page={search.page}
              pageSize={PAGE_SIZE}
              total={total}
              onPageChange={(page) => setSearch({ page })}
              className="w-full"
            />
          }
        />
      </Panel>
    </>
  );
}

/* ── Inventory ────────────────────────────────────────────────────────────── */

function InventoryReport() {
  const q = useQuery({
    queryKey: ['reports', 'inventory'],
    queryFn: () => api.get<RankedRow[]>('/reports/inventory'),
  });

  const rows = useMemo(() => q.data ?? [], [q.data]);
  const totals = useMemo(
    () =>
      rows.reduce(
        (acc, r) => ({ units: acc.units + r.count, value: acc.value + r.revenue }),
        { units: 0, value: 0 }
      ),
    [rows]
  );

  const columns: Array<Column<RankedRow>> = [
    { id: 'label', header: 'Product', cell: (r) => r.label },
    {
      id: 'count',
      header: 'On hand',
      cell: (r) => (
        <span className={r.count < 0 ? 'text-negative' : undefined}>{qty(r.count)}</span>
      ),
      mono: true,
      align: 'right',
      width: '9rem',
    },
    {
      id: 'unit',
      header: 'Unit cost',
      cell: (r) => (r.count ? money(Math.round(r.revenue / r.count)) : '—'),
      mono: true,
      align: 'right',
      width: '9rem',
      hideBelow: 'lg',
    },
    {
      id: 'revenue',
      header: 'Value at cost',
      cell: (r) => money(Math.round(r.revenue)),
      mono: true,
      align: 'right',
      width: '11rem',
    },
  ];

  return (
    <>
      <Callout
        tone="neutral"
        icon={<Info />}
        title="Tracked products only, valued at cost"
      >
        Only products with inventory tracking switched on appear here; untracked and
        unclassified products are absent by design, not missing. Value is on-hand × the
        product's current cost, so a product with no cost on file contributes nothing. A
        negative on-hand is a real data problem worth chasing, not a display artefact.
      </Callout>

      <StatRow>
        <Stat label="Tracked products" value={num(rows.length)} />
        <Stat label="Units on hand" value={qty(totals.units)} />
        <Stat label="Inventory value" value={money(Math.round(totals.value))} />
      </StatRow>

      <Panel title="Top 15 by value at cost">
        <BarRank
          rows={rows.slice(0, 15).map((r) => ({
            id: r.id,
            label: r.label,
            value: Math.round(r.revenue),
            hint: `${qty(r.count)} on hand`,
          }))}
          format={moneyCompact}
          empty="No tracked products carrying value."
        />
      </Panel>

      <Panel title="All tracked products" bleed className="min-h-72">
        <DataTable
          rows={q.data}
          columns={columns}
          rowKey={(r) => r.id}
          loading={q.isLoading}
          error={q.error}
          onRetry={() => void q.refetch()}
          keyboard={false}
          rowTone={(r) => (r.count < 0 ? 'negative' : null)}
          empty={{
            icon: <Table2 />,
            title: 'Nothing is tracked yet',
            description:
              'Turn on inventory tracking for a product to make it appear in this report.',
          }}
          footer={
            rows.length ? (
              <span className="font-mono tabular-nums">
                {num(rows.length)} products · {qty(totals.units)} units ·{' '}
                {money(Math.round(totals.value))}
              </span>
            ) : null
          }
        />
      </Panel>
    </>
  );
}

/* ── Cashiers ─────────────────────────────────────────────────────────────── */

function CashiersReport({ search }: { search: ReportsSearch }) {
  const params = filterParams(search, 'cashiers');
  const q = useQuery({
    queryKey: ['reports', 'cashiers', params],
    queryFn: () => api.get<CashierRow[]>('/reports/cashiers', { params }),
  });

  const rows = q.data ?? [];
  const assigned = rows.filter((r) => !r.is_unassigned);
  const unassigned = rows.find((r) => r.is_unassigned) ?? null;
  const totalRevenue = rows.reduce((a, r) => a + r.revenue, 0);
  const totalCount = rows.reduce((a, r) => a + r.count, 0);
  const sales = salesLabel(!isCatalogScoped(search));

  const columns: Array<Column<CashierRow>> = [
    {
      id: 'label',
      header: 'Cashier',
      cell: (r) =>
        r.is_unassigned ? (
          <span className="flex items-center gap-2">
            <span className="text-ink-muted">No cashier on the ticket</span>
            <Badge tone="warn">Unassigned</Badge>
          </span>
        ) : (
          r.label
        ),
    },
    {
      id: 'count',
      header: 'Tickets',
      cell: (r) => num(r.count),
      mono: true,
      align: 'right',
      width: '9rem',
    },
    {
      id: 'avg',
      header: 'Avg ticket',
      cell: (r) => (r.count ? money(Math.round(r.revenue / r.count)) : '—'),
      mono: true,
      align: 'right',
      width: '9rem',
      hideBelow: 'lg',
    },
    {
      id: 'share',
      header: 'Share',
      cell: (r) => (totalRevenue ? percent((r.revenue / totalRevenue) * 100) : '—'),
      mono: true,
      align: 'right',
      width: '8rem',
      hideBelow: 'md',
    },
    {
      id: 'revenue',
      header: sales,
      cell: (r) => money(r.revenue),
      mono: true,
      align: 'right',
      width: '10rem',
    },
  ];

  return (
    <>
      <ScopeNote search={search} />
      {unassigned ? (
        <Callout
          tone="warn"
          icon={<Info />}
          title={`${money(unassigned.revenue)} across ${num(unassigned.count)} tickets has no cashier`}
        >
          Tickets with no <code className="font-mono">cashier_id</code> — legacy imports
          and any register that finalised without an identified operator — are bucketed
          here rather than dropped, so this report still adds up to store sales. They
          cannot be attributed retroactively from this screen.
        </Callout>
      ) : null}

      <StatRow>
        <Stat label="Cashiers" value={num(assigned.length)} />
        <Stat label="Transactions" value={num(totalCount)} />
        <Stat label={sales} value={money(totalRevenue)} />
        <Stat
          label="Unattributed"
          value={unassigned ? money(unassigned.revenue) : money(0)}
          tone={unassigned && unassigned.revenue !== 0 ? 'warn' : undefined}
          sub={
            unassigned && totalRevenue
              ? percent((unassigned.revenue / totalRevenue) * 100)
              : undefined
          }
        />
      </StatRow>

      <Panel title={`${sales} by cashier`} description={`${search.from} → ${search.to}`}>
        <BarRank
          rows={rows.map((r) => ({
            id: r.id ?? 'unassigned',
            label: r.is_unassigned ? 'Unassigned' : r.label,
            value: r.revenue,
            tone: r.is_unassigned ? 'neutral' : 'signal',
            hint: `${num(r.count)} tickets`,
          }))}
          format={moneyCompact}
          empty="No completed sales in this range."
        />
      </Panel>

      <Panel title="Detail" bleed>
        <DataTable
          rows={q.data}
          columns={columns}
          rowKey={(r) => r.id ?? 'unassigned'}
          loading={q.isLoading}
          error={q.error}
          onRetry={() => void q.refetch()}
          keyboard={false}
          rowTone={(r) => (r.is_unassigned ? 'warn' : null)}
          empty={{
            icon: <Table2 />,
            title: 'No completed sales in this range',
            description: 'Widen the date range or clear the catalog filters.',
          }}
          footer={
            rows.length ? (
              <span className="font-mono tabular-nums">
                {num(totalCount)} tickets · {money(totalRevenue)}
              </span>
            ) : null
          }
        />
      </Panel>
    </>
  );
}

/* ── Tenders ──────────────────────────────────────────────────────────────── */

/**
 * Display names for the `tender_type` enum.
 *
 * There is deliberately no `debit`: a debit card is recorded as `credit` with
 * `meta.cardType`, and the API rejects `debit` outright. The label says "card"
 * rather than "credit" so nobody reads this table as "we take no debit", and
 * the raw enum value is shown beside it so a row can be matched to the CSV.
 */
const TENDER_LABEL: Record<string, string> = {
  cash: 'Cash',
  credit: 'Card',
  ebt: 'EBT',
  wic: 'WIC',
  gc: 'Gift card',
  house: 'House account',
  check: 'Check',
  coupon: 'Coupon',
};

function tenderLabel(value: string): string {
  return TENDER_LABEL[value] ?? humanize(value);
}

/** A share of the whole, drawn as a proportion rather than left as a number to
 *  compare in your head. Signal is the accent this app already uses for "this
 *  is the measure"; the track is a sunken surface, so both themes hold. */
function ShareMeter({ share }: { share: number }) {
  const pct = Math.max(0, Math.min(1, share)) * 100;
  return (
    <span className="flex items-center justify-end gap-2">
      <span
        className="bg-surface-sunken border-line h-1.5 w-14 shrink-0 overflow-hidden rounded-full border"
        aria-hidden
      >
        <span
          className="bg-signal block h-full rounded-full"
          style={{ width: `${pct}%` }}
        />
      </span>
      <span className="w-12 shrink-0 text-right">{percent(pct)}</span>
    </span>
  );
}

function TendersReport({ search }: { search: ReportsSearch }) {
  const params = filterParams(search, 'tenders');
  const q = useQuery({
    queryKey: ['reports', 'tenders', params],
    queryFn: () => api.get<TenderRow[]>('/reports/tenders', { params }),
  });

  const rows = useMemo(() => q.data ?? [], [q.data]);
  const totals = useMemo(
    () =>
      rows.reduce(
        (acc, r) => ({
          revenue: acc.revenue + r.revenue,
          count: acc.count + r.count,
          transactionCount: acc.transactionCount + r.transactionCount,
        }),
        { revenue: 0, count: 0, transactionCount: 0 }
      ),
    [rows]
  );
  const cash = rows.find((r) => r.label === 'cash') ?? null;
  const card = rows.find((r) => r.label === 'credit') ?? null;
  /* Tender rows beyond one per receipt *of the same type* — the only split
     figure this payload can support. A receipt paid part cash, part card
     contributes one row to each type and shows up in neither of those gaps. */
  const repeats = totals.count - totals.transactionCount;

  const columns: Array<Column<TenderRow>> = [
    {
      id: 'label',
      header: 'Tender',
      cell: (r) => (
        <span className="flex items-center gap-2">
          {tenderLabel(r.label)}
          <Code>{r.label}</Code>
        </span>
      ),
    },
    {
      id: 'count',
      header: 'Tenders',
      cell: (r) => num(r.count),
      mono: true,
      align: 'right',
      width: '7rem',
    },
    {
      id: 'receipts',
      header: 'Receipts',
      // The receipt count is the link: "show me the cash tickets" is the
      // question this tab makes someone ask, and /transactions now takes a
      // tender_type filter, so the answer is one click away instead of a
      // manual re-filter.
      cell: (r) => (
        <span>
          <Link
            to="/transactions"
            search={{
              range: 'custom' as const,
              from: search.from,
              to: search.to,
              tenderType: r.label,
              status: 'all' as const,
              q: '',
              page: 1,
            }}
            className="text-signal-ink hover:underline"
          >
            {num(r.transactionCount)}
          </Link>
          {r.count > r.transactionCount ? (
            <span className="text-ink-faint ml-1.5 text-2xs">
              +{num(r.count - r.transactionCount)}
            </span>
          ) : null}
        </span>
      ),
      mono: true,
      align: 'right',
      width: '8rem',
    },
    {
      id: 'avg',
      header: 'Avg per receipt',
      cell: (r) =>
        r.transactionCount ? money(Math.round(r.revenue / r.transactionCount)) : '—',
      mono: true,
      align: 'right',
      width: '10rem',
      hideBelow: 'lg',
    },
    {
      id: 'share',
      header: 'Share of value',
      cell: (r) => <ShareMeter share={r.share} />,
      mono: true,
      align: 'right',
      width: '11rem',
      hideBelow: 'md',
    },
    {
      id: 'revenue',
      header: 'Tendered',
      cell: (r) => (
        <span className={r.revenue < 0 ? 'text-negative' : undefined}>
          {money(r.revenue)}
        </span>
      ),
      mono: true,
      align: 'right',
      width: '10rem',
    },
  ];

  return (
    <>
      <Callout
        tone="info"
        icon={<Info />}
        title="Tenders, receipts, and the missing debit"
      >
        A <strong className="text-ink font-medium">tender</strong> is one payment line; a
        receipt can carry several.{' '}
        <strong className="text-ink font-medium">Receipts</strong> counts the distinct
        tickets a tender type appeared on, so the column does not add up to store receipts
        — a ticket paid part cash, part card is counted once in each row, and this report
        cannot say how many tickets were split that way. There is no <Code>debit</Code>{' '}
        tender: a debit card is recorded as <Code>credit</Code> with the card type in{' '}
        <Code>meta.cardType</Code>, so debit is inside Card, not missing.
      </Callout>

      <StatRow>
        <Stat
          label="Tendered"
          value={money(totals.revenue)}
          sub={`${num(totals.count)} tenders`}
        />
        <Stat
          label="Cash"
          value={money(cash?.revenue ?? 0)}
          sub={cash ? `${percent(cash.share * 100)} of value` : 'no cash in this range'}
        />
        <Stat
          label="Card"
          value={money(card?.revenue ?? 0)}
          sub={card ? `${percent(card.share * 100)} of value` : 'no card in this range'}
        />
        <Stat
          label="Repeat tenders"
          value={num(repeats)}
          sub="same tender twice on one receipt"
        />
      </StatRow>

      <Panel
        title="Tender mix"
        description={`${search.from} → ${search.to} · cash carries its own colour because it is the bar that becomes a deposit run`}
      >
        <BarRank
          rows={rows.map((r) => ({
            id: r.label,
            label: tenderLabel(r.label),
            value: r.revenue,
            tone: r.label === 'cash' ? 'info' : 'signal',
            hint: `${percent(r.share * 100)} of tendered value · ${num(r.count)} tenders on ${num(r.transactionCount)} receipts`,
          }))}
          format={moneyCompact}
          empty="Nothing was tendered in this range."
        />
      </Panel>

      <Panel title="By tender type" bleed>
        <DataTable
          rows={q.data}
          columns={columns}
          rowKey={(r) => r.label}
          loading={q.isLoading}
          error={q.error}
          onRetry={() => void q.refetch()}
          keyboard={false}
          empty={{
            icon: <Table2 />,
            title: 'No completed sales in this range',
            description: 'Widen the date range to see how tickets were paid.',
          }}
          footer={
            rows.length ? (
              <span className="font-mono tabular-nums">
                {num(rows.length)} tender types · {num(totals.count)} tenders ·{' '}
                {money(totals.revenue)}
              </span>
            ) : null
          }
        />
      </Panel>
    </>
  );
}

/* ── Voids ────────────────────────────────────────────────────────────────── */

/**
 * A count that opens the receipts behind it.
 *
 * Zero is not a link — an action either works or is absent, and a link to an
 * empty list is a dead end. Neither is the Unassigned bucket: `/transactions`
 * filters by a cashier id, and "no cashier at all" is not an id it accepts.
 */
function VoidCountLink({
  count,
  cashierId,
  status,
  from,
  to,
}: {
  count: number;
  cashierId: number | null;
  status: 'voided' | 'refund';
  from: string;
  to: string;
}) {
  if (count === 0) return <span className="text-ink-faint">0</span>;
  if (cashierId === null) return <>{num(count)}</>;
  return (
    <Link
      to="/transactions"
      search={{
        range: 'custom' as const,
        from,
        to,
        status,
        cashier: cashierId,
        q: '',
        page: 1,
      }}
      className="text-signal-ink hover:underline"
    >
      {num(count)}
    </Link>
  );
}

function VoidsReport({ search }: { search: ReportsSearch }) {
  const params = filterParams(search, 'voids');
  const q = useQuery({
    queryKey: ['reports', 'voids', params],
    queryFn: () => api.get<VoidRow[]>('/reports/voids', { params }),
  });
  /* The denominator for a rate. Eight voids means one thing on eighty tickets
     and another on eight hundred, and /reports/voids cannot say which — so the
     completed-ticket count comes from /reports/cashiers over the same dates.
     Purely additive: if it fails or the cashier has no sales, the rate is —. */
  const volume = useQuery({
    queryKey: ['reports', 'cashiers', params],
    queryFn: () => api.get<CashierRow[]>('/reports/cashiers', { params }),
    retry: false,
  });

  const rows = useMemo(() => q.data ?? [], [q.data]);
  const totals = useMemo(
    () =>
      rows.reduce(
        (acc, r) => ({
          voidCount: acc.voidCount + r.voidCount,
          voidTotal: acc.voidTotal + r.voidTotal,
          refundCount: acc.refundCount + r.refundCount,
          refundTotal: acc.refundTotal + r.refundTotal,
        }),
        { voidCount: 0, voidTotal: 0, refundCount: 0, refundTotal: 0 }
      ),
    [rows]
  );

  /* Keyed by id, never by label — `id: null` is the synthetic bucket and a real
     cashier could perfectly well be named "Unassigned". */
  const sales = useMemo(() => {
    const map = new Map<number, number>();
    for (const c of volume.data ?? []) if (c.id !== null) map.set(c.id, c.count);
    return map;
  }, [volume.data]);

  const voidRate = useCallback(
    (row: VoidRow): number | null => {
      if (row.id === null) return null;
      const completed = sales.get(row.id);
      if (completed === undefined || completed === 0) return null;
      return (row.voidCount / completed) * 100;
    },
    [sales]
  );

  const unassigned = rows.find((r) => r.is_unassigned) ?? null;

  /* The server orders by total activity (voids + refunds); a ranking chart is
     ordered by the bar it draws, or the lengths and the order disagree. */
  const rank: BarRankRow[] = useMemo(
    () =>
      [...rows]
        .sort((a, b) => b.voidTotal - a.voidTotal)
        .map((r) => ({
          id: r.id ?? 'unassigned',
          label: r.is_unassigned ? 'Unassigned' : r.label,
          value: r.voidTotal,
          tone: r.is_unassigned ? 'neutral' : 'warn',
          hint: `${num(r.voidCount)} voids · ${num(r.refundCount)} refunds ${money(r.refundTotal)}`,
        })),
    [rows]
  );

  const columns: Array<Column<VoidRow>> = [
    {
      id: 'label',
      header: 'Cashier',
      cell: (r) =>
        r.is_unassigned ? (
          <span className="flex items-center gap-2">
            <span className="text-ink-muted">No cashier on the ticket</span>
            <Badge tone="warn">Unassigned</Badge>
          </span>
        ) : (
          r.label
        ),
    },
    {
      id: 'voidCount',
      header: 'Voids',
      cell: (r) => (
        <VoidCountLink
          count={r.voidCount}
          cashierId={r.id}
          status="voided"
          from={search.from}
          to={search.to}
        />
      ),
      mono: true,
      align: 'right',
      width: '6rem',
    },
    {
      id: 'voidTotal',
      header: 'Voided value',
      cell: (r) => money(r.voidTotal),
      mono: true,
      align: 'right',
      width: '10rem',
    },
    {
      id: 'rate',
      header: 'Void rate',
      cell: (r) => {
        const rate = voidRate(r);
        return rate === null ? <span className="text-ink-faint">—</span> : percent(rate);
      },
      mono: true,
      align: 'right',
      width: '11rem',
      hideBelow: 'lg',
    },
    {
      id: 'refundCount',
      header: 'Refunds',
      cell: (r) => (
        <VoidCountLink
          count={r.refundCount}
          cashierId={r.id}
          status="refund"
          from={search.from}
          to={search.to}
        />
      ),
      mono: true,
      align: 'right',
      width: '7rem',
    },
    {
      id: 'refundTotal',
      header: 'Refunded value',
      cell: (r) => (
        <span className={r.refundTotal < 0 ? 'text-negative' : undefined}>
          {money(r.refundTotal)}
        </span>
      ),
      mono: true,
      align: 'right',
      width: '11rem',
    },
  ];

  return (
    <>
      <Callout
        tone="warn"
        icon={<Info />}
        title="These tickets are in none of the other numbers on this screen"
      >
        Every other tab filters to completed sales and refunds; voided tickets are
        excluded there on purpose, because a void that stayed in revenue would subtract
        itself twice. So these figures do not reconcile against Sales, Financial or
        Cashiers, and they are not meant to — this is the one place voided activity is
        visible at all. Voided value stays positive (a void keeps the original ticket's
        sign) while refunded value is negative; the two are never summed together.
      </Callout>

      {unassigned && unassigned.count > 0 ? (
        <Callout
          tone="neutral"
          icon={<Info />}
          title={`${num(unassigned.count)} of these tickets have no cashier on them`}
        >
          Tickets with no <Code>cashier_id</Code> — legacy imports, or a register that
          finalised without an identified operator — are bucketed here rather than
          dropped. They cannot be opened from this screen: the receipt list filters by a
          cashier id, and "no cashier" is not one.
        </Callout>
      ) : null}

      <StatRow>
        <Stat
          label="Voided tickets"
          value={num(totals.voidCount)}
          tone={totals.voidCount > 0 ? 'warn' : undefined}
        />
        <Stat label="Voided value" value={money(totals.voidTotal)} />
        <Stat label="Refunded tickets" value={num(totals.refundCount)} />
        <Stat
          label="Refunded value"
          value={money(totals.refundTotal)}
          tone={totals.refundTotal < 0 ? 'negative' : undefined}
        />
      </StatRow>

      <Panel
        title="Voided value by cashier"
        description={`${search.from} → ${search.to} · longest bar is the largest exposure, not necessarily the highest rate`}
      >
        <BarRank
          rows={rank}
          format={moneyCompact}
          empty="Nothing was voided or refunded in this range."
        />
      </Panel>

      <Panel
        title="By cashier"
        description="Void rate is voided tickets against that cashier's own completed tickets over the same dates, so a busy lane and a quiet one can be read side by side. A count opens the receipts behind it."
        bleed
      >
        <DataTable
          rows={q.data}
          columns={columns}
          rowKey={(r) => r.id ?? 'unassigned'}
          loading={q.isLoading}
          error={q.error}
          onRetry={() => void q.refetch()}
          keyboard={false}
          rowTone={(r) => (r.is_unassigned ? 'warn' : null)}
          empty={{
            icon: <Table2 />,
            title: 'Nothing was voided or refunded in this range',
            description:
              'That is the good outcome. Widen the dates to look further back.',
          }}
          footer={
            rows.length ? (
              <span className="font-mono tabular-nums">
                {num(totals.voidCount)} voids {money(totals.voidTotal)} ·{' '}
                {num(totals.refundCount)} refunds {money(totals.refundTotal)}
              </span>
            ) : null
          }
        />
      </Panel>
    </>
  );
}
